import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  Grid
} from '@mui/material';
import { io } from 'socket.io-client';
import { bindGameResync } from '../../../../utils/bindGameResync';
import {
  ArrowBack as BackIcon,
  FlashOn as RapidIcon,
  EmojiEvents as TrophyIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  MusicNote as MusicIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components
const WaitingContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#000000',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#0b1120',
  border: '1px solid rgba(148, 163, 184, 0.25)',
  borderRadius: 16,
  marginBottom: 24,
  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.6)',
}));

const EqualHeightCardContent = styled(CardContent)(({ theme }) => ({
  textAlign: 'center',
  minHeight: 160,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

const LeaderboardCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#020617',
  border: '1px solid rgba(148, 163, 184, 0.3)',
  borderRadius: 16,
  marginBottom: 24,
}));

const RapidQuizWaiting = ({ session, participant, onBack, onQuestionReceived, lastPushedAt: parentLastPushedAt }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [sessionStatus] = useState('waiting');
  const [currentSong] = useState(null);
  const [participantCount, setParticipantCount] = useState(0);
  // Polling for questions removed; state retained for UI only
  // const [lastQuestionsSnapshot, setLastQuestionsSnapshot] = useState(null);

  // Fetch leaderboard data
  const fetchLeaderboard = useCallback(async () => {
    if (!session?.session_id) return;
    
    try {
      setLoadingLeaderboard(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${session.session_id}/leaderboard`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch leaderboard');
      }
      
      const data = await response.json();
      setLeaderboard(data.leaderboard || []);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    } finally {
      setLoadingLeaderboard(false);
    }
  }, [session?.session_id]);

  // const lastSeenPushedAtRef = useRef(null);

  // Remove questions polling; parent listens via socket and invokes onQuestionReceived
  // const checkForActiveQuestions = useCallback(async () => { /* no-op */ }, []);

  // Update participant count
  const updateParticipantCount = useCallback(async () => {
    if (!session?.session_id) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${session.session_id}/participants`);
      
      if (response.ok) {
        const data = await response.json();
        setParticipantCount(data.participants?.length || 0);
      }
    } catch (err) {
      console.error('Error fetching participant count:', err);
    }
  }, [session?.session_id]);

  // Replace polling with WebSocket listeners for real-time updates
  useEffect(() => {
    if (!session?.session_id) return;
    
    // Initial fetch for immediate display
    fetchLeaderboard();
    updateParticipantCount();

    let socket;
    let unbindResync = () => {};
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      socket = io(baseUrl, { transports: ['websocket'] });
      
      socket.on('connect', () => {
        socket.emit('rapid:join', { sessionId: session.session_id });
        fetchLeaderboard();
        updateParticipantCount();
      });
      unbindResync = bindGameResync(() => {
        if (socket.connected) socket.emit('rapid:join', { sessionId: session.session_id });
        fetchLeaderboard();
        updateParticipantCount();
      });
      
      socket.on('rapid:leaderboard', (payload) => {
        if (payload && payload.session_id === session.session_id) {
          setLeaderboard(payload.leaderboard || []);
        }
      });
      
      socket.on('rapid:participants', (payload) => {
        if (payload && payload.session_id === session.session_id) {
          setParticipantCount(payload.participants?.length || 0);
        }
      });
      
    } catch (error) {
      console.warn('Socket connection failed, falling back to polling:', error);
      // Fallback to polling if socket fails
      const lbTimer = setInterval(fetchLeaderboard, 3000);
      const pTimer = setInterval(updateParticipantCount, 5000);
      
      return () => {
        clearInterval(lbTimer);
        clearInterval(pTimer);
      };
    }

    return () => {
      unbindResync();
      try {
        if (socket) {
          socket.emit('rapid:leave', { sessionId: session.session_id });
          socket.disconnect();
        }
      } catch (error) {
        console.warn('Socket cleanup error:', error);
      }
    };
  }, [session?.session_id, fetchLeaderboard, updateParticipantCount]);

  // Get user's position in leaderboard
  const getUserPosition = () => {
    if (!participant?.participant_id) return null;
    
    const userIndex = leaderboard.findIndex(entry => 
      entry.participant_id === participant.participant_id
    );
    
    return userIndex >= 0 ? userIndex + 1 : null;
  };

  const userPosition = getUserPosition();
  const userEntry = leaderboard.find(entry => 
    entry.participant_id === participant?.participant_id
  );

  return (
    <WaitingContainer>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'rgba(15, 23, 42, 0.9)',
          borderRadius: 2,
          mb: 3,
          border: '1px solid rgba(148, 163, 184, 0.35)',
        }}
      >
        <Toolbar>
          <IconButton onClick={onBack} sx={{ mr: 2, color: '#e5e7eb' }}>
            <BackIcon />
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, color: '#e5e7eb', fontWeight: 600 }}
          >
            {session?.quiz_name || 'Rapid Quiz'}
          </Typography>
          <IconButton
            onClick={fetchLeaderboard}
            disabled={loadingLeaderboard}
            sx={{ color: '#e5e7eb' }}
          >
            <RefreshIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg">
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <RapidIcon sx={{ fontSize: 72, color: '#f97316', mb: 1 }} />
          <Typography
            variant="h4"
            sx={{ color: '#e5e7eb', fontWeight: 600, mb: 1 }}
          >
            Waiting for questions
          </Typography>
          <Typography variant="body1" sx={{ color: '#9ca3af' }}>
            The host will push song questions shortly. Keep this tab open and be ready to answer.
          </Typography>
        </Box>

        {/* Status Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={4}>
            <StyledCard>
              <EqualHeightCardContent>
                <MusicIcon sx={{ fontSize: 40, color: '#f97316' }} />
                <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>
                  Session status
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: '#e5e7eb', fontWeight: 600 }}
                >
                  {sessionStatus === 'question_active' ? '🎵' : '⏳'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                  {sessionStatus === 'question_active' ? 'Question Active!' : 'Waiting...'}
                </Typography>
              </EqualHeightCardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <StyledCard>
              <EqualHeightCardContent>
                <PeopleIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
                <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>
                  Players joined
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: '#e5e7eb', fontWeight: 600 }}
                >
                  {participantCount}
                </Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                  are in this game
                </Typography>
              </EqualHeightCardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <StyledCard>
              <EqualHeightCardContent>
                <TrophyIcon sx={{ fontSize: 40, color: '#facc15' }} />
                <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>
                  Your position
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: '#e5e7eb', fontWeight: 600 }}
                >
                  {userPosition ? `#${userPosition}` : '-'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                  on the live leaderboard
                </Typography>
              </EqualHeightCardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Current Song Info */}
        {currentSong && (
          <StyledCard sx={{ mb: 4 }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  color: '#f97316',
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <PlayIcon sx={{ mr: 1 }} />
                Current Song
              </Typography>
              <Typography
                variant="h5"
                sx={{ color: '#e5e7eb', fontWeight: 600, mb: 0.5 }}
              >
                {currentSong.name}
              </Typography>
              <Typography variant="body1" sx={{ color: '#9ca3af', mb: 2 }}>
                by {currentSong.artist}
              </Typography>
              <Chip 
                label="Question Active!" 
                sx={{ 
                  backgroundColor: '#22c55e', 
                  color: '#022c22',
                  fontWeight: 600,
                }} 
              />
            </CardContent>
          </StyledCard>
        )}

        {/* Instructions */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{ color: '#e5e7eb', mb: 2.5, fontWeight: 600 }}
            >
              How this round works
            </Typography>
            <List>
              <ListItem sx={{ py: 1 }}>
                <Typography variant="body1" sx={{ color: '#9ca3af' }}>
                  🎵 Questions appear the moment the host sends them.
                </Typography>
              </ListItem>
              <ListItem sx={{ py: 1 }}>
                <Typography variant="body1" sx={{ color: '#9ca3af' }}>
                  ⚡ Answer quickly to stay ahead on the leaderboard.
                </Typography>
              </ListItem>
              <ListItem sx={{ py: 1 }}>
                <Typography variant="body1" sx={{ color: '#9ca3af' }}>
                  🏆 Scores update in real-time as everyone answers.
                </Typography>
              </ListItem>
              <ListItem sx={{ py: 1 }}>
                <Typography variant="body1" sx={{ color: '#9ca3af' }}>
                  📱 Please stay on this page and avoid refreshing.
                </Typography>
              </ListItem>
            </List>
          </CardContent>
        </StyledCard>

        {/* Live Leaderboard */}
        <LeaderboardCard>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#e5e7eb',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <TrophyIcon sx={{ color: '#facc15' }} />
                Live leaderboard
              </Typography>
              <Chip 
                label={`${leaderboard.length} players`}
                sx={{ backgroundColor: '#f97316', color: '#111827' }}
              />
            </Box>

            {loadingLeaderboard ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#f97316' }} />
                <Typography sx={{ color: '#e5e7eb', ml: 2 }}>
                  Loading leaderboard...
                </Typography>
              </Box>
            ) : leaderboard.length > 0 ? (
              <List>
                {leaderboard.slice(0, 10).map((entry, index) => {
                  const isCurrentUser = entry.participant_id === participant?.participant_id;
                  const position = index + 1;
                  
                  return (
                    <ListItem 
                      key={entry.participant_id} 
                      sx={{ 
                        py: 2,
                        backgroundColor: isCurrentUser
                          ? 'rgba(249, 115, 22, 0.08)'
                          : 'transparent',
                        borderRadius: '8px',
                        mb: 1,
                        border: isCurrentUser
                          ? '1px solid rgba(249, 115, 22, 0.7)'
                          : '1px solid transparent',
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ 
                          bgcolor:
                            position === 1
                              ? '#facc15'
                              : position === 2
                              ? '#e5e7eb'
                              : position === 3
                              ? '#fb923c'
                              : '#0f172a',
                          width: 40,
                          height: 40,
                          fontSize: '1rem',
                          fontWeight: 'bold'
                        }}>
                          {position}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body1"
                            sx={{ color: '#e5e7eb', fontWeight: 500 }}
                          >
                            {(entry.fname && entry.lname) ? `${entry.fname} ${entry.lname}` : 
                             entry.guest_name || 
                             entry.anonymous_id || 
                             'Anonymous'}
                            {isCurrentUser && (
                              <Chip 
                                label="You" 
                                size="small" 
                                sx={{ 
                                  ml: 1,
                                  backgroundColor: '#f97316',
                                  color: '#111827',
                                  fontSize: '0.7rem'
                                }} 
                              />
                            )}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                            {entry.current_score || 0} points
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                })}
                {leaderboard.length > 10 && (
                  <ListItem sx={{ py: 2 }}>
                    <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', width: '100%' }}>
                      ... and {leaderboard.length - 10} more players
                    </Typography>
                  </ListItem>
                )}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  No players on leaderboard yet
                </Typography>
              </Box>
            )}

            {/* User's current stats */}
            {userEntry && (
              <>
                <Divider sx={{ my: 2, borderColor: '#1f2937' }} />
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#020617',
                    borderRadius: '8px',
                    border: '1px solid rgba(148, 163, 184, 0.35)',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ color: '#f97316', mb: 0.5, fontWeight: 600 }}
                  >
                    Your Stats
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#e5e7eb' }}>
                    Position: #{userPosition} • Score: {userEntry.current_score || 0} points
                  </Typography>
                </Box>
              </>
            )}
          </CardContent>
        </LeaderboardCard>

        {/* Footer */}
        <Box textAlign="center" mt={4}>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            Waiting for the host to start the quiz… ⏳
          </Typography>
        </Box>
      </Container>
    </WaitingContainer>
  );
};

export default RapidQuizWaiting;
